import {DATE_VALIDATION_TYPE, DIGIT_REGEX, VALIDATION_MESSAGES} from '@/utils/constants/commonConstants'
import {createValidationFunction, isPublished} from '@/utils/commons'

const VALIDATION_FUNCTIONS = {
  validateSixDigits: createValidationFunction(DIGIT_REGEX.SIX_DIGITS_DECIMAL_TWO_PLACES, VALIDATION_MESSAGES.POINTS_EARNED_6_DIGITS),
  validateSixDigitsAlt: createValidationFunction(
    DIGIT_REGEX.SIX_DIGITS,
    VALIDATION_MESSAGES.POINTS_EARNED_6_DIGITS_ALT
  ),
  validateTwoDigits: createValidationFunction(
    DIGIT_REGEX.TWO_DIGITS,
    VALIDATION_MESSAGES.DAILY_LOGIN_LIMIT_2_DIGITS
  ),
  validateFourDigits: createValidationFunction(
    DIGIT_REGEX.FOUR_DIGITS,
    VALIDATION_MESSAGES.ALL_TIME_LOGIN_LIMIT_4_DIGITS
  ),
}

// UI Schema for Activity Selection
export const activitySelectionUiSchema = (formData) => {
  const isPublishedStatus = isPublished(formData?.status);

  return {
    type: 'HorizontalLayout',
    className: 'w-auto',
    elements: [
      {
        type: 'HorizontalLayout',
        className: 'avail-first-row',
        elements: [
          {
            type: 'Control',
            scope: '#/properties/activityType',
            component: 'dropdownControl',
            placeholder: 'Choose Activity Type',
            label: 'Activity Type *',
            className: 'box-size-width text-sm dropdown-max-width',
            isDark: true,
            disabled: isPublishedStatus, // Disable if status is Published
            validationRules: [{type: 'required', msg: ` is required`}],
          },
        ],
      },
    ],
  };
}

// UI Schema for Login Activity
export const loginActivityJsonSchema = () => ({
  type: 'object',
  properties: {
    pointsEarned: {
      errorMessage: 'This field is required',
      type: 'string',
      restrictDecimal: false,
    },
    dailyLoginLimit: {
      type: 'string',
      restrictDecimal: true,
    },
    allTimeLoginLimit: {
      type: 'string',
      restrictDecimal: true,
    },
  },
  required: [],
})

export const loginActivityUiSchema = () => ({
  type: 'VerticalLayout',
  className: 'w-full',
  elements: [
    {
      type: 'HorizontalLayout',
      className: '',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/pointsEarned',
          component: 'textfield',
          placeholder: 'Enter points',
          label: 'Points Earned *',
          className: 'box-size-width text-sm',
          isDark: true,
          required: true,
          validationRules: [
            {type: 'required', msg: ` is required`},
            {type: 'number', msg: ` must be a number`},
            {
              type: 'custom',
              validate: VALIDATION_FUNCTIONS.validateSixDigits,
            },
          ],
        },
        {
          type: 'Control',
          scope: '#/properties/dailyLoginLimit',
          component: 'textfield',
          placeholder: 'Enter daily limit',
          label: 'Daily Login Limit',
          className: 'box-size-width text-sm',
          isDark: true,
          validationRules: [
            {type: 'number', msg: ` must be a number`},
            {
              type: 'custom',
              validate: VALIDATION_FUNCTIONS.validateTwoDigits,
            },
          ],
        },
        {
          type: 'Control',
          scope: '#/properties/allTimeLoginLimit',
          component: 'textfield',
          placeholder: 'Enter total limit',
          label: 'All Time Login Limit',
          className: 'box-size-width text-sm',
          isDark: true,
          helpText: 'Max number of logins to receive points',
          validationRules: [
            {type: 'number', msg: ` must be a number`},
            {
              type: 'custom',
              validate: VALIDATION_FUNCTIONS.validateFourDigits,
            },
          ],
        },
      ],
    },
  ],
})

// Schema for Welcome Activity
export const welcomeActivityJsonSchema = {
  type: 'number',
  properties: {
    pointsEarned: {
      type: 'string',
      restrictDecimal: false,
    },
  },
  required: [],
}

// UI Schema for Welcome Activity
export const welcomeActivityUiSchema = {
  type: 'HorizontalLayout',
  className: 'partner-filters',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'avail-first-row',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/pointsEarned',
          component: 'textfield',
          placeholder: 'Enter points',
          label: 'Points Earned *',
          className: 'box-size-width text-sm',
          isDark: true,
          validationRules: [
            {type: 'required', msg: ` is required`},
            {type: 'number', msg: ` must be a number`},
            {
              type: 'custom',
              validate: VALIDATION_FUNCTIONS.validateSixDigits,
            },
          ],
        },
      ],
    },
  ],
}

// Schema for Welcome Activity Availability
export const welcomeActivityAvailabilityJsonSchema = {
  type: 'object',
  properties: {
    startDate: {
      type: 'string',
    },
    hasEndDate: {
      type: 'boolean',
      default: false,
    },
  },
  required: [],
}

export const welcomeActivityHasEndDateSchema = {
  type: 'object',
  properties: {
    hasEndDate: {
      type: 'boolean',
      default: false,
    },
  },
  required: [],
}

export const welcomeActivityEndDateJsonSchema = {
  type: 'object',
  properties: {
    endDate: {
      type: 'string',
    },
  },
  required: [],
}

// UI Schema for Welcome Activity Availability
export const welcomeActivityAvailabilityUiSchema = (endDate, startDate, formData) => {
  const isPublishedStatus = isPublished(formData?.status);

  return {
    type: 'HorizontalLayout',
    className: 'partner-filters',
    elements: [
      {
        type: 'HorizontalLayout',
        className: 'items-center',
        elements: [
          {
            type: 'Control',
            scope: '#/properties/startDate',
            component: 'datepicker',
            className: 'box-size-width',
            placeholder: 'Choose date',
            label: 'Active Start Date *',
            isDark: true,
            required: false,
            disabled: isPublishedStatus, // Disable if status is Published
            disabledHelperText: isPublishedStatus ?  '' :'Please select end date before selecting start date.',
            dependentDate: endDate || startDate,
            validationRules: [
              {type: 'required', msg: ` is required`},
              {type: DATE_VALIDATION_TYPE.START_DATE, msg: ` cannot be in the past.`, endDate: endDate},
              {type: DATE_VALIDATION_TYPE.START_DATE_TODAY, msg: `  Target can only start from a date in future`},
            ],
          },
        ],
      },
    ],
  };
}

export const welcomeActivityHasEndUiSchema = {
  type: 'HorizontalLayout',
  className: 'partner-filters',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'items-center',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/hasEndDate',
          component: 'toggle',
          label: 'Has End Date',
          className: 'mt-8 justify-between  w-44',
        },
      ],
    },
  ],
}

export const welcomeActivityEndDateUiSchema = (startDate, endDate) => ({
  type: 'HorizontalLayout',
  className: 'partner-filters',
  elements: [
    {
      type: 'VerticalLayout',
      className: '',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/endDate',
          component: 'datepicker',
          className: 'box-size-width',
          placeholder: 'Choose date',
          label: 'Active End Date *',
          isDark: true,
          required: false,
          disabled: !startDate,
          dependentDate: endDate || startDate,
          disabledHelperText: 'Please select start date before selecting end date.',
          validationRules: [
            {type: 'required', msg: ` is required`},
            {type: DATE_VALIDATION_TYPE.END_DATE, msg: ` can't be less than Start date.`, startDate: startDate},
          ],
        },
      ],
    },
  ],
})

// Schema for Points On Order Delivery Activity
export const pointsOnOrderDeliveryActivityJsonSchema = {
  type: 'object',
  properties: {
    activityName: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required',
    },
    orderType: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required',
      options: [
        {
          id: 1,
          displayText: 'Online',
        },
        {
          id: 2,
          displayText: 'Offline',
        },
        {
          id: 3,
          displayText: 'Both',
        },
      ],
    },
  },
  required: [],
}

// UI Schema for Points On Order Delivery Activity
export const pointsOnOrderDeliveryActivityUiSchema = formData => ({
  type: 'HorizontalLayout',
  className: 'w-full',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'w-full',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/activityName',
          component: 'textfield',
          placeholder: 'Enter activity name',
          label: 'Activity Name *',
          className: 'box-size-width hide-cross-icon text-sm',
          isDark: true,
          disabled: formData?.status != undefined, // Not editable if status is not null
        },
        {
          type: 'Control',
          scope: '#/properties/orderType',
          component: 'dropdownControl',
          placeholder: 'Select order type',
          label: 'Order Type *',
          className: 'box-size-width text-sm',
          isDark: true,
        },
      ],
    },
  ],
})

// Schema for Availability
export const availabilityJsonSchema = {
  type: 'object',
  properties: {
    startDate: {
      type: 'string',
    },
    endDate: {
      type: 'string',
    },
    receivePoints: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required',
      options: [
        {value: 'Instantly', displayText: 'Instantly'},
        {value: 'Daily', displayText: 'Daily'},
        {value: 'Weekly', displayText: 'Weekly'},
        {value: 'Biweekly', displayText: 'Biweekly'},
        {value: 'Monthly', displayText: 'Monthly'},
      ],
    },
  },
  required: [],
}

// UI Schema for Availability
export const availabilityUiSchema = (startDate, endDate, formData) => ({
  type: 'VerticalLayout',
  className: 'partner-filters',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'avail-first-row',
      elements: availabilityDateControls(startDate, endDate, formData),
    },
  ],
})

// Shared date controls for availability UI schemas
const availabilityDateControls = (startDate, endDate, formData) => {
  const isPublishedStatus = isPublished(formData?.status);

  return [
    {
      type: 'Control',
      scope: '#/properties/startDate',
      component: 'datepicker',
      className: 'box-size-width',
      placeholder: 'Choose date',
      label: 'Active Start Date *',
      isDark: true,
      required: false,
      disabled: isPublishedStatus, // Disable if status is Published
      disabledHelperText: isPublishedStatus ?  '' :'Please select end date before selecting start date.',
      dependentDate: endDate,
      validationRules: [
        {type: 'required', msg: ` is required`},
        {type: DATE_VALIDATION_TYPE.START_DATE, msg: ` can't be in the past.`, endDate: endDate},
        {type: DATE_VALIDATION_TYPE.START_DATE_TODAY, msg: `  Target can only start from a date in future`},
      ],
    },
    {
      type: 'Control',
      scope: '#/properties/endDate',
      component: 'datepicker',
      placeholder: 'Choose date',
      className: 'box-size-width',
      label: 'Active End Date *',
      disabled: !startDate,
      disabledHelperText: 'Please select start date before selecting end date.',
      isDark: true,
      required: false,
      dependentDate: startDate,
      validationRules: [
        {type: 'required', msg: ` is required`},
        {type: DATE_VALIDATION_TYPE.END_DATE, msg: ` can't be less than Start date.`, startDate: startDate},
      ],
    },
  ];
}

export const allAvailabilityUiSchema = (startDate, endDate, formData) => ({
  type: 'VerticalLayout',
  className: 'w-3/4',
  elements: [
    {
      type: 'HorizontalLayout',
      className: '',
      elements: [
        ...availabilityDateControls(startDate, endDate, formData),
        {
          type: 'Control',
          scope: '#/properties/receivePoints',
          component: 'dropdownControl',
          placeholder: 'Choose Frequency',
          label: 'Frequency *',
          className: 'box-size-width mt-1',
          isDark: true,
          required: true,
          validationRules: [{type: 'required', msg: ` is required`}],
        },
      ],
    },
  ],
})

// Schema for Points allocation
export const pointsAllocationJsonSchema = {
  type: 'object',
  properties: {
    localCurrency: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required',
    },
    pointsEarned: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required',
    },
  },
  required: [],
}
export const pointsAllocationJsonSchemaForEG = {
  type: 'object',
  properties: {
    numberOfCases: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required',
      restrictDecimal: true,
    },
    pointsEarned: {
      type: 'string',
      helperText: '',
      errorMessage: 'This field is required',
      restrictDecimal: false,
    },
  },
  required: [],
}

// UI Schema for Points Allocation
export const pointsAllocationUiSchema = {
  type: 'VerticalLayout',
  className: 'partner-filters flex w-auto',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'flex gap-2',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/localCurrency',
          component: 'textfield',
          placeholder: 'Enter amount',
          label: 'Local Currency *',
          className: 'box-size-width text-sm',
          isDark: true,
          required: true,
          validationRules: [
            {type: 'required', msg: ` is required`},
            {type: 'decimal', msg: 'must be a number'},
          ],
        },
        {
          type: 'Control',
          scope: '#/properties/pointsEarned',
          component: 'textfield',
          placeholder: 'Enter points',
          label: 'Points Earned *',
          className: 'hide-cross-icon box-size-width text-sm',
          isDark: true,
          required: true,
          validationRules: [
            {type: 'required', msg: ` is required`},
            {type: 'decimal', msg: 'must be a number'},
            {
              type: 'custom',
              validate: VALIDATION_FUNCTIONS.validateSixDigits,
            },
          ],
        },
      ],
    },
  ],
}
export const pointsAllocationUiSchemaForEG = {
  type: 'VerticalLayout',
  className: 'partner-filters w-full sm:w-2/4 flex flex-col',
  elements: [
    {
      type: 'HorizontalLayout',
      className: 'w-4/4 flex gap-4',
      elements: [
        {
          type: 'Control',
          scope: '#/properties/numberOfCases',
          component: 'textfield',
          placeholder: 'Enter amount',
          label: 'Number Of Cases*',
          className: 'text-sm w-full',
          isDark: true,
          required: true,
          validationRules: [
            {type: 'required', msg: ` is required`},
            {type: 'decimal', msg: 'must be a number'},
          ],
        },
        {
          type: 'Control',
          scope: '#/properties/pointsEarned',
          component: 'textfield',
          placeholder: 'Enter points',
          label: 'Points Earned *',
          className: 'hide-cross-icon text-sm w-full',
          isDark: true,
          required: true,
          validationRules: [
            {type: 'required', msg: ` is required`},
            {type: 'decimal', msg: 'must be a number'},
            {
              type: 'custom',
              validate: VALIDATION_FUNCTIONS.validateSixDigits,
            },
          ],
        },
      ],
    },
  ],
}
